import { differenceInSeconds } from 'date-fns';
import { useMemo, useState } from 'react';
import { useInterval } from 'react-use';

import { Pagination } from '@sorare/core/src/atoms/navigation/Pagination';
import { useBackendPagination } from '@sorare/core/src/contexts/graphql-search';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { isTypes } from '@sorare/core/src/lib/gql';

import type { BackendCardResultsProps } from '#src/searchCards/AdvancedCardSearch/types.ts';
import { CardPreviewGrid } from '#src/searchCards/CardPreviewGrid/index.tsx';
import { CARDS_QUERY } from '#src/searchCards/CardResultsFromGraphQL/index.tsx';

const FINISHED_AUCTION_GRACE_SECONDS = 10;
const FINISHED_AUCTION_SWEEP_INTERVAL = 5_000;

export const BackendCardResultsFromGraphQL = ({
  hitSlugs,
  searchLoading,
  nbHits,
  stacksBySlug,
  hideOwner,
  galleryOwnerSlug,
  hideSorareUser,
  topic,
  showDesktopFilter,
  onlyPrimary,
  editableLists,
  sport,
  selectionMode,
  setSelectionMode,
  totalXpNeeded,
  canDrop,
  canLevelUp,
  hideScoutShortcuts,
  onToggleCard,
  PrependedItemComponent,
  selectedCardSlugs,
  showActionButtonOnFirstCard,
  removeFinishedAuctions,
}: BackendCardResultsProps) => {
  const { isIosApp, isAndroidApp, googlePlayStoreCountryCode } =
    useIsMobileApp();
  const { page, pages, refine } = useBackendPagination();

  const [nowMs, setNowMs] = useState(() => Date.now());
  useInterval(
    () => {
      setNowMs(Date.now());
    },
    removeFinishedAuctions ? FINISHED_AUCTION_SWEEP_INTERVAL : null
  );

  const { data, loading: dataLoading } = useQuery(
    CARDS_QUERY,
    {
      variables: {
        slugs: hitSlugs,
        editableLists: Boolean(editableLists),
        onlyPrimary: Boolean(onlyPrimary),
        isIosApp,
        isAndroidApp,
        googlePlayStoreCountryCode,
      },
      skip: searchLoading || hitSlugs.length === 0,
    },
    { skipCacheAndNetworkLoadingOverride: true }
  );

  const items = useMemo(() => {
    if (!data?.anyCards) {
      return [];
    }
    const isFinishedAuction = (card: (typeof data.anyCards)[number]) => {
      if (!isTypes(card, ['Card', 'NBACard', 'BaseballCard'])) return false;
      const { latestEnglishAuction } = card;
      return (
        !!latestEnglishAuction &&
        !latestEnglishAuction.open &&
        differenceInSeconds(nowMs, latestEnglishAuction.endDate) >
          FINISHED_AUCTION_GRACE_SECONDS
      );
    };
    const cardsBySlug = new Map(data?.anyCards.map(c => [c.slug, c] as const));
    return hitSlugs
      .map(slug => {
        const card = cardsBySlug.get(slug);
        if (!card) {
          return card;
        }
        if (removeFinishedAuctions && isFinishedAuction(card)) {
          return undefined;
        }
        const stack = stacksBySlug?.[slug];
        if (!stack || stack.count <= 1) {
          return card;
        }
        return {
          ...card,
          stack: {
            algoliaDistinctKey: stack.distinctKey ?? '',
            count: stack.count,
          },
        };
      })
      .filter(Boolean);
  }, [data?.anyCards, hitSlugs, stacksBySlug, removeFinishedAuctions, nowMs]);

  return (
    <>
      <CardPreviewGrid
        items={items}
        isBackend
        hideOwner={hideOwner}
        galleryOwnerSlug={galleryOwnerSlug}
        topic={topic}
        hideSorareUser={hideSorareUser}
        loading={searchLoading || dataLoading}
        showDesktopFilter={showDesktopFilter}
        editableLists={editableLists}
        onlyPrimary={!!onlyPrimary}
        sport={sport}
        selectionMode={selectionMode}
        setSelectionMode={setSelectionMode}
        totalXpNeeded={totalXpNeeded}
        hideScoutShortcuts={hideScoutShortcuts}
        canDrop={canDrop}
        canLevelUp={canLevelUp}
        onToggleCard={onToggleCard}
        selectedCardSlugs={selectedCardSlugs}
        showActionButtonOnFirstCard={showActionButtonOnFirstCard}
        prependedItem={
          PrependedItemComponent ? (
            <PrependedItemComponent total={nbHits} />
          ) : undefined
        }
        pagination={
          <Pagination currentPage={page - 1} pages={pages} onSelect={refine} />
        }
      />
    </>
  );
};
